import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import styles from "./BlogDetail.module.css";
import { Helmet } from "react-helmet";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { defaultAxios } from "../apis";
import remarkGfm from "remark-gfm";

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [markdown, setMarkdown] = useState("");
  const [articleMeta, setArticleMeta] = useState(null);

  const language = window.location.pathname.startsWith("/kr") ? "kr" : "en";

  // Fetch article metadata directly from API using slug — no localStorage
  useEffect(() => {
    if (!slug) return;

    defaultAxios
      .get(`/api/blogs/`, { params: { language } })
      .then((res) => {
        const results = res.data?.results || [];
        const found = results.find((a) => a.slug === slug);
        setArticleMeta(found || null);
      })
      .catch((err) => console.error("Failed to fetch article metadata", err));
  }, [slug, language]);

  // Fetch markdown content
  useEffect(() => {
    if (!slug) return;

    defaultAxios
      .get(`/api/blogs/${slug}/`, {
        params: { language },
        responseType: "text",
      })
      .then((res) => setMarkdown(res.data))
      .catch(() => setMarkdown("Sorry, article not found."));
  }, [slug, language]);

  return (
    <div className={styles.blogWrapper}>
      {articleMeta && (
        <Helmet>
          <title>{articleMeta.title || "Xenorize"}</title>
          <meta name="description" content={articleMeta.metaDescription} />
          <meta name="keywords" content={articleMeta.keywords?.join(", ")} />
          <link
            rel="canonical"
            href={`https://xenorize.com${language === "kr" ? "/kr" : ""}/digest/${articleMeta.slug}`}
          />
        </Helmet>
      )}

      {articleMeta?.heroImage && (
        <div className={styles.heroContainer}>
          <img
            src={articleMeta.heroImage}
            alt={articleMeta.title}
            className={styles.heroImage}
          />
          <h1 className={styles.heroTitle}>{articleMeta.title}</h1>
        </div>
      )}

      <div className={styles.blogContent}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeRaw,
            [
              rehypeSanitize,
              {
                ...defaultSchema,
                attributes: {
                  ...defaultSchema.attributes,
                  "*": [...(defaultSchema.attributes["*"] || []), "style"],
                  div: [...(defaultSchema.attributes.div || []), "style"],
                  img: [
                    ...(defaultSchema.attributes.img || []),
                    "style",
                    "width",
                    "height",
                  ],
                },
              },
            ],
          ]}
          components={{
            h1: ({ node, ...props }) => <h1 className={styles.h1} {...props} />,
            h2: ({ node, ...props }) => <h2 className={styles.h2} {...props} />,
            h3: ({ node, ...props }) => <h3 className={styles.h3} {...props} />,
            h4: ({ node, ...props }) => <h4 className={styles.h4} {...props} />,
            p: ({ node, ...props }) => <p className={styles.p} {...props} />,
            ul: ({ node, ...props }) => <ul className={styles.ul} {...props} />,
            li: ({ node, ...props }) => <li className={styles.li} {...props} />,
            img: ({ node, ...props }) => (
              <img className={styles.image} {...props} />
            ),
            table: ({ node, ...props }) => (
              <table className={styles.table} {...props} />
            ),
            thead: ({ node, ...props }) => (
              <thead className={styles.thead} {...props} />
            ),
            tbody: ({ node, ...props }) => (
              <tbody className={styles.tbody} {...props} />
            ),
            tr: ({ node, ...props }) => <tr className={styles.tr} {...props} />,
            th: ({ node, ...props }) => <th className={styles.th} {...props} />,
            td: ({ node, ...props }) => <td className={styles.td} {...props} />,
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default BlogDetailPage;
